import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

type TelemetryProperties = Record<string, string | number | boolean | undefined | null>;

type TelemetryUser = {
  id: string | null;
};

const enabledValue = process.env.REACT_APP_APPLICATIONINSIGHTS_ENABLED;
const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
const environmentName = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV;
const releaseVersion = process.env.REACT_APP_RELEASE_VERSION;
const samplingPercentageValue = Number(
  process.env.REACT_APP_APPLICATIONINSIGHTS_SAMPLING_PERCENTAGE
);

const isTelemetryEnabled =
  enabledValue?.toLowerCase() === "true" && Boolean(connectionString);
const samplingPercentage =
  Number.isFinite(samplingPercentageValue) &&
    samplingPercentageValue > 0 &&
    samplingPercentageValue <= 100
    ? samplingPercentageValue
    : 100;

let appInsights: ApplicationInsights | null = null;
let globalErrorListenersRegistered = false;

export const reactPlugin = new ReactPlugin();

function getBaseProperties(): TelemetryProperties {
  return {
    environment: environmentName,
    appName: "clubr.web",
    releaseVersion,
  };
}

function normalizeProperties(properties?: TelemetryProperties): TelemetryProperties {
  return {
    ...getBaseProperties(),
    ...properties,
  };
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error("Unknown client-side error");
  }
}

export function initializeTelemetry(): void {
  if (!isTelemetryEnabled || appInsights) {
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,
      samplingPercentage,
      extensions: [reactPlugin],
    },
  });

  appInsights.loadAppInsights();
  appInsights.trackPageView({
    properties: normalizeProperties(),
  });
}

export function registerGlobalErrorHandlers(): void {
  if (globalErrorListenersRegistered) {
    return;
  }

  globalErrorListenersRegistered = true;

  window.addEventListener("error", event => {
    trackException(event.error || event.message, {
      source: "window.error",
      filename: event.filename,
      lineNumber: event.lineno,
      columnNumber: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", event => {
    trackException(event.reason, {
      source: "window.unhandledrejection",
    });
  });
}

export function trackException(
  error: unknown,
  properties?: TelemetryProperties,
  severityLevel: SeverityLevel = SeverityLevel.Error
): void {
  if (!appInsights) {
    return;
  }

  appInsights.trackException(
    {
      error: normalizeError(error),
      severityLevel,
    },
    normalizeProperties(properties)
  );
}

export function trackEvent(name: string, properties?: TelemetryProperties): void {
  if (!appInsights) {
    return;
  }

  appInsights.trackEvent({ name }, normalizeProperties(properties));
}

export function trackMetric(
  name: string,
  average: number,
  properties?: TelemetryProperties
): void {
  if (!appInsights) {
    return;
  }

  appInsights.trackMetric({ name, average }, normalizeProperties(properties));
}

export function setTelemetryUser(user: TelemetryUser): void {
  if (!appInsights || !user.id) {
    return;
  }

  appInsights.setAuthenticatedUserContext(user.id, undefined, true);
}

export function clearTelemetryUser(): void {
  if (!appInsights) {
    return;
  }

  appInsights.clearAuthenticatedUserContext();
}

export function isApplicationInsightsEnabled(): boolean {
  return isTelemetryEnabled;
}
